import React, { forwardRef, useMemo } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import EmptyList from "go-core/components/EmptyList";
import { useWindowSize } from "go-core/components/useWindowSize";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { parseToSelectedDateFilter } from "../../services/charts/chartService";
import { DashboardChartActions } from "../../services/charts/reducer";
import useDashboardContext from "../../services/hooks/useDashboardContext";
import { useDashboardDraggable } from "../../services/hooks/useDashboardDraggable";
import {
	AggregateOption,
	DashboardCardConfig,
	DashboardGridItem,
	DraggableComponentProps,
	salesAggregateOptions,
} from "../../services/types";
import { renderAggregateValue } from "../../utils/reportConfigUtils";
import { AggregateValuePercentageChange } from "../Charts/AggregateValuePercentageChange";
import DashboardReportSalesAdvancedChart from "./DashboardReportSalesAdvancedChart";
import ReportItemWrapper from "./ReportItemWrapper/ReportItemWrapper";

const DraggableDashboardReportSalesAdvanced = forwardRef<HTMLDivElement, DraggableComponentProps>(
	({ style, className, onMouseDown, onMouseUp, onTouchEnd, setNonMobileLayout }, ref): JSX.Element => {
		const {
			state,
			state: {
				chartsConfig: {
					reportSalesAdvanced: { aggregate: selectedTab },
				},
			},
			isEditMode,
			dispatch,
		} = useDashboardContext();
		const report = state?.data?.sales_advanced_reports?.reports[0];
		const { t } = useTranslation();
		const chartDataRangeType = state?.chartsConfig?.reportSalesAdvanced.range;
		const chartType = state?.chartsConfig.reportSalesAdvanced.type;
		const selectedFilters = parseToSelectedDateFilter(state.filtersConfig.selectedFilter.dateRange);
		const { currency } = useSelector(selectOrganization);
		const isMobile = useWindowSize().isMobile;
		const refChildren = ref && "current" in ref ? ref.current?.children : undefined;
		const { gridItemZIndex, cursorType, onMouseEnter, onMouseLeave, onCustomMouseDown, onCustomMouseUp } =
			useDashboardDraggable(
				DashboardGridItem.SALES_ADVANCED,
				setNonMobileLayout,
				refChildren,
				onMouseDown,
				onMouseUp
			);

		const renderView = useMemo(() => {
			if (!state?.data?.sales_advanced_reports || !report?.sub_report || !report.sub_report.length) {
				return (
					<div className={"mt-3"}>
						<h5>{t("modules.dashboard.field.sale.title")}</h5>
						<EmptyList />
					</div>
				);
			}
			return (
				<DashboardReportSalesAdvancedChart
					chartDataRangeType={chartDataRangeType}
					aggregate={selectedTab}
					data={state?.data.sales_advanced_reports}
					selectedFilters={selectedFilters}
				/>
			);
		}, [state?.data?.sales_advanced_reports, selectedTab]);

		const tabItemClickHandler = (newSelectedTab: AggregateOption) => {
			if (isEditMode) return;

			dispatch({
				type: DashboardChartActions.CHANGE_SALES_ADVANCED_REPORT,
				data: {
					aggregate: newSelectedTab,
				},
			});
		};

		const renderHeader = useMemo(() => {
			if (!report?.sub_report || !report.sub_report.length || !report.aggregate?.sales) {
				return null;
			}
			return (
				<>
					<div className={`report-tabs ${isMobile ? "report-tabs--mobile" : ""}`}>
						{salesAggregateOptions.map((opt) => {
							const optionParts = opt.includes(".")
								? opt.split(".").map((optionPart) => optionPart)
								: [opt];
							const currentPeriodValue = optionParts.reduce(
								(prev, curr) => prev?.[curr],
								report.aggregate.sales
							);
							const lastPeriodValue = optionParts.reduce(
								(compareSaleAggregate, saleAggregate) => compareSaleAggregate?.[saleAggregate],
								report.compare_aggregate.sales
							);
							const textMutedClass = isEditMode ? "text-muted" : "";

							return (
								<div
									className={`report-tab-item ${isEditMode ? "" : "non-hover-element"} ${
										!isEditMode && selectedTab === opt && "active"
									}`}
									key={opt}
									onClick={() => tabItemClickHandler(opt)}
								>
									<div className={`aggregate-title ${textMutedClass}`}>
										{t(`modules.dashboard.field.${opt}.title`)}
									</div>
									<h4
										className={`aggregate-value ${textMutedClass} ${
											selectedTab === opt ? "active" : ""
										}`}
									>
										{renderAggregateValue(currentPeriodValue, currency || "")}
									</h4>
									<AggregateValuePercentageChange
										currentPeriodValue={currentPeriodValue}
										lastPeriodValue={lastPeriodValue}
									/>
								</div>
							);
						})}
					</div>
				</>
			);
		}, [selectedTab, state?.data?.sales_advanced_reports, chartType, currency, isMobile, isEditMode]);

		const config = {
			title: renderHeader,
			reportNameToRedirectTo: selectedTab === "extended.average_per_guest_money" ? "guests" : "orders",
			type: "SALES_ADVANCED_REPORT",
			chartDataRangeType,
			chartType,
			chartConfigName: "reportSalesAdvanced",
			cursorType,
		} as DashboardCardConfig;

		return (
			<Card
				className={`${className} ${
					isEditMode ? "dashboard-edit-mode-element-active" : ""
				} report-sales-advanced`}
				style={{ ...style, zIndex: gridItemZIndex, cursor: cursorType }}
				onMouseDown={onCustomMouseDown}
				onMouseUp={onCustomMouseUp}
				onTouchEnd={onTouchEnd}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				ref={ref}
			>
				<ReportItemWrapper config={config}>{renderView}</ReportItemWrapper>
			</Card>
		);
	}
);

export default DraggableDashboardReportSalesAdvanced;

DraggableDashboardReportSalesAdvanced.displayName = "DraggableDashboardReportSalesAdvanced";

// t("modules.dashboard.field.total_money.title");
// t("modules.dashboard.field.transaction_count.title");
// t("modules.dashboard.field.net_profit_money.title");
// t("modules.dashboard.field.average_money.title");
// t("modules.dashboard.field.extended.average_per_guest_money.title");
